import type { FC } from 'react';
import React, { useContext, Fragment } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { B50, B200, B400, N20A, N30, N500 } from '@atlaskit/theme/colors';

import { useSessionData } from '@confluence/session-data';
import { useIsProductAdmin } from '@confluence/current-user';

import { FiltersContext } from './FiltersContext';

export const PillFilter = {
	ALL: 'all',
	COMMUNAL: 'communal',
	WATCHING: 'watching',
	STARRED: 'starred',
	PERSONAL: 'personal',
	ARCHIVED: 'archived',
	TRASHED: 'trashed',
};

const i18n = defineMessages({
	all: {
		id: 'space-directory.filters.all',
		defaultMessage: 'All',
		description:
			"This is the default option which indicates the lack of a filter, meaning that 'All' spaces will be visible.",
	},
	communal: {
		id: 'space-directory.filters.communal',
		defaultMessage: 'Communal',
		description:
			"Name of a filter that will show non-personal spaces only. ('communal spaces', formerly known as 'site spaces')",
	},
	watching: {
		id: 'space-directory.filters.watching',
		defaultMessage: 'Watching',
		description: 'Name of a filter that will show spaces the user is watching (subscribed to)',
	},
	starred: {
		id: 'space-directory.filters.starred',
		defaultMessage: 'Starred',
		description: 'Name of a filter that will show the user has starred (favorited)',
	},
	personal: {
		id: 'space-directory.filters.personal.v2',
		defaultMessage: 'Personal',
		description:
			"Name of a filter that will show personal spaces (previously this read 'Personal spaces' but now just says 'Personal')",
	},
	archived: {
		id: 'space-directory.filters.archived',
		defaultMessage: 'Archived',
		description: 'Name of a filter that will show spaces that have been archived',
	},
	trashed: {
		id: 'space-directory.filters.trashed',
		defaultMessage: 'Trashed',
		description: 'Name of a filter that will show spaces that have been trashed',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PillButton = styled.button<{
	isSelected?: boolean;
}>({
	outline: 'none',
	boxSizing: 'border-box',
	padding: `${token('space.050', '4px')} ${token('space.100', '8px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props) =>
		props.isSelected
			? token('color.background.selected', B50)
			: token('color.background.neutral', N20A),
	display: 'inline-flex',
	border: 'none',
	borderRadius: '13px',
	font: token('font.body.UNSAFE_small'),
	height: '26px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: (props) =>
		props.isSelected ? token('color.text.selected', B400) : token('color.text', N500),
	cursor: 'pointer',
	marginTop: '0px',
	marginLeft: token('space.050', '4px'),
	userSelect: 'none',
	maxWidth: '100%',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	verticalAlign: 'top',
	'&:focus': {
		boxShadow: `0 0 0 2px ${token('color.border.selected', B200)}`,
	},
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		backgroundColor: (props) =>
			props.isSelected
				? token('color.background.selected.hovered', B50)
				: token('color.background.neutral.hovered', N30),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:first-of-type': {
		marginLeft: 0,
	},
});

export const PillFilters: FC = () => {
	const intl = useIntl();
	const { filters, updateSpaceType } = useContext(FiltersContext);

	const { isLicensed } = useSessionData();
	const { isProductAdmin } = useIsProductAdmin();

	const licensedPillOptions = [
		{ label: intl.formatMessage(i18n.watching), value: PillFilter.WATCHING },
		{ label: intl.formatMessage(i18n.starred), value: PillFilter.STARRED },
	];

	const pillOptions = [
		{ label: intl.formatMessage(i18n.all), value: PillFilter.ALL },
		...(isLicensed ? licensedPillOptions : []),
		{ label: intl.formatMessage(i18n.communal), value: PillFilter.COMMUNAL },
		{
			label: intl.formatMessage(i18n.personal),
			value: PillFilter.PERSONAL,
		},
		{ label: intl.formatMessage(i18n.archived), value: PillFilter.ARCHIVED },
	];

	if (isProductAdmin) {
		pillOptions.push({
			label: intl.formatMessage(i18n.trashed),
			value: PillFilter.TRASHED,
		});
	}

	return (
		<Fragment>
			{pillOptions.map((option) => {
				const selected = filters.spaceType === option.value;
				return (
					<PillButton
						key={option.value}
						onClick={() => updateSpaceType(option.value)}
						isSelected={selected}
						aria-selected={selected}
					>
						{option.label}
					</PillButton>
				);
			})}
		</Fragment>
	);
};
